import React, { Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { I18nProvider } from "@/lib/i18n/i18n";
import { AuthProvider, isAuthenticated, useAuth } from "@/lib/auth";
import { canAccessPath, homePathFor } from "@/lib/route-access";

const Login = React.lazy(() => import("@/pages/login"));
const Forbidden = React.lazy(() => import("@/pages/forbidden"));

/**
 * Garde globale : non authentifié hors de "/" → redirige vers le login ;
 * déjà authentifié sur "/" → saute vers la première page autorisée au rôle.
 *
 * NB : la destination dépend du rôle (homePathFor). Le tableau de bord
 * diocésain n'est pas accessible à tous — un membre de la commission
 * doctrinale y serait accueilli par un « Accès refusé ».
 */
function RouteGuard({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    if (location !== "/" && !isAuthenticated()) {
      setLocation("/");
    } else if (location === "/" && isAuthenticated()) {
      setLocation(homePathFor(user?.role));
    }
  }, [location, setLocation, user?.role]);
  return <>{children}</>;
}

// Lazy-load all admin pages
const AdminActualites = React.lazy(() => import("@/pages/admin-actualites"));
const AdminActualitesEditeur = React.lazy(() => import("@/pages/admin-actualites-editeur"));
const AdminAudit = React.lazy(() => import("@/pages/admin-audit"));
const AdminComptesEspacePretres = React.lazy(() => import("@/pages/admin-comptes-espace-pretres"));
const AdminSiteEdaci = React.lazy(() => import("@/pages/admin-site-edaci"));
const AdminApplications = React.lazy(() => import("@/pages/admin-applications"));
const AdminNotificationsPush = React.lazy(() => import("@/pages/admin-notifications-push"));
const AdminEspacePretres = React.lazy(() => import("@/pages/admin-espace-pretres"));
const AdminEspacePretresEditeur = React.lazy(() => import("@/pages/admin-espace-pretres-editeur"));
const AdminSms = React.lazy(() => import("@/pages/admin-sms"));
const AdminCalendrierEditorial = React.lazy(() => import("@/pages/admin-calendrier-editorial"));
const AdminCollectes = React.lazy(() => import("@/pages/admin-collectes"));
const AdminContributions = React.lazy(() => import("@/pages/admin-contributions"));
const AdminDashboardDiocese = React.lazy(() => import("@/pages/admin-dashboard-diocese"));
const AdminStatistiques = React.lazy(() => import("@/pages/admin-statistiques"));
const AdminMediatheque = React.lazy(() => import("@/pages/admin-mediatheque"));
const AdminTextesPages = React.lazy(() => import("@/pages/admin-textes-pages"));
const AdminDocuments = React.lazy(() => import("@/pages/admin-documents"));
const AdminDoyennes = React.lazy(() => import("@/pages/admin-doyennes"));
const AdminEvenements = React.lazy(() => import("@/pages/admin-evenements"));
const AdminInformationsUrgentes = React.lazy(() => import("@/pages/admin-informations-urgentes"));
const AdminFeatureFlags = React.lazy(() => import("@/pages/admin-feature-flags"));
const AdminCampagneApplications = React.lazy(() => import("@/pages/admin-campagne-applications"));
const AdminNewsletter = React.lazy(() => import("@/pages/admin-newsletter"));
const AdminMessages = React.lazy(() => import("@/pages/admin-messages"));
const AdminSupport = React.lazy(() => import("@/pages/admin-support"));
const AdminParametres = React.lazy(() => import("@/pages/admin-parametres"));
const AdminPelerinages = React.lazy(() => import("@/pages/admin-pelerinages"));
const AdminRoles = React.lazy(() => import("@/pages/admin-roles"));
const AdminSolidariteProjets = React.lazy(() => import("@/pages/admin-solidarite-projets"));
const AdminUtilisateurs = React.lazy(() => import("@/pages/admin-utilisateurs"));
const AdminVideos = React.lazy(() => import("@/pages/admin-videos"));
const AdminUtilisateurDetail = React.lazy(() => import("@/pages/admin-utilisateur-detail"));
const AdminVicariats = React.lazy(() => import("@/pages/admin-vicariats"));
const AdminContenuDoctrinal = React.lazy(() => import("@/pages/admin-contenu-doctrinal"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AdminRoutes() {
  // NB : chaque page fournit son propre <AppShell> (avec titre/sous-titre).
  // Ne PAS re-wrapper ici, sinon deux sidebars s'affichent (bug layout).
  const [location] = useLocation();
  const { user } = useAuth();

  // Garde de route par rôle : le menu masque déjà les sections interdites,
  // mais une URL directe rendait la page (403 backend seulement). On rend
  // une page 403 honnête à la place (cf. lib/route-access.ts).
  if (user && !canAccessPath(location, user.role)) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Forbidden />
      </Suspense>
    );
  }

  return (
      <Suspense fallback={<PageFallback />}>
        <Switch>
          {/* Tableau de bord */}
          <Route path="/admin/dashboard-diocese" component={AdminDashboardDiocese} />
          <Route path="/admin/statistiques" component={AdminStatistiques} />
          <Route path="/admin/mediatheque" component={AdminMediatheque} />
          <Route path="/admin/textes-pages" component={AdminTextesPages} />

          {/* Contenu */}
          <Route path="/admin/actualites" component={AdminActualites} />
          <Route path="/admin/actualites/editeur/:id?" component={AdminActualitesEditeur} />
          <Route path="/admin/videos" component={AdminVideos} />
          <Route path="/admin/evenements" component={AdminEvenements} />
          <Route path="/admin/informations-urgentes" component={AdminInformationsUrgentes} />
          <Route path="/admin/documents" component={AdminDocuments} />
          <Route path="/admin/espace-pretres" component={AdminEspacePretres} />
          <Route path="/admin/espace-pretres/:id" component={AdminEspacePretresEditeur} />
          <Route path="/admin/newsletter" component={AdminNewsletter} />
          <Route path="/admin/messages" component={AdminMessages} />
          <Route path="/admin/support" component={AdminSupport} />
          <Route path="/admin/support/:id" component={AdminSupport} />
          <Route path="/admin/calendrier-editorial" component={AdminCalendrierEditorial} />
          <Route path="/admin/contributions" component={AdminContributions} />

          {/* Administration */}
          <Route path="/admin/utilisateurs" component={AdminUtilisateurs} />
          <Route path="/admin/utilisateurs/:id" component={AdminUtilisateurDetail} />
          <Route path="/admin/roles" component={AdminRoles} />
          <Route path="/admin/parametres" component={AdminParametres} />
          <Route path="/admin/feature-flags" component={AdminFeatureFlags} />
          <Route path="/admin/campagne-applications" component={AdminCampagneApplications} />
          <Route path="/admin/audit" component={AdminAudit} />
          <Route path="/admin/sms" component={AdminSms} />
          <Route path="/admin/comptes-espace-pretres" component={AdminComptesEspacePretres} />
          <Route path="/admin/site-edaci" component={AdminSiteEdaci} />
          <Route path="/admin/applications" component={AdminApplications} />
          <Route path="/admin/notifications-push" component={AdminNotificationsPush} />
          <Route path="/admin/notifications-push/:id" component={AdminNotificationsPush} />

          {/* Territoire */}
          <Route path="/admin/vicariats" component={AdminVicariats} />
          <Route path="/admin/doyennes" component={AdminDoyennes} />

          {/* Missions */}
          <Route path="/admin/pelerinages" component={AdminPelerinages} />
          <Route path="/admin/collectes" component={AdminCollectes} />
          <Route path="/admin/solidarite-projets" component={AdminSolidariteProjets} />

          {/* Doctrine */}
          <Route path="/admin/contenu-doctrinal" component={AdminContenuDoctrinal} />

          {/* Par défaut → première page autorisée au rôle. Ne PAS rendre le
              tableau de bord directement : un rôle qui n'y a pas droit
              (commission doctrinale) le verrait via n'importe quelle URL
              inconnue, en contournant la garde. */}
          <Route><Redirect to={homePathFor(user?.role)} /></Route>
        </Switch>
      </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <RouteGuard>
            <Suspense fallback={<PageFallback />}>
              <Switch>
                <Route path="/" component={Login} />
                <Route>
                  <AdminRoutes />
                </Route>
              </Switch>
            </Suspense>
          </RouteGuard>
          <Toaster />
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
